import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ChatComposer } from '../../components/ChatComposer';
import { NeoMarkdown } from '../../components/NeoChat';
import { readNeoStream } from '../../lib/neoStream';
import { adminApi } from '../adminApi';
import {
  apiErrorMessage,
  parseChangeDrafts,
  parseStoredMessages,
  usedChangeFromError,
  type NeoChange,
  type NeoChangeDraft,
} from '../neoChanges';

// Admin Neo (ADR 0231): describe a product change, Neo drafts it as a card,
// and only the admin's Start (or Revise) runs the coding agent. Talks to
// POST /v1/admin/neo/chat/stream with the same SSE framing as owner Neo
// (lib/neoStream). The server owns history; the page keeps only the id.
// Cards are stored with their reply, so a reopened chat shows them again, and
// a card already started links to its change instead (ADR 0251).

export interface ChatMessage {
  key: string;
  role: 'user' | 'assistant';
  content: string;
  drafts: NeoChangeDraft[];
  error?: boolean;
}

interface Conversation {
  conversationId: string | null;
  messages: unknown;
}

let keySeq = 0;
const nextKey = () => `m${(keySeq += 1)}`;

export const NOT_CONFIGURED_TITLE = 'Neo changes are not configured on this server';

/**
 * How a card's Start or Revise button looks. The shared disabled style shows
 * the busy cursor, which is right only while a request is in flight; a button
 * disabled because the server has no Neo changes says so instead. Inline, so
 * it scopes to these buttons and beats the shared `:disabled` rule.
 */
export function draftActionState(busy: boolean, configured: boolean) {
  if (busy) return { disabled: true, cursor: 'progress', title: undefined } as const;
  if (!configured) {
    return { disabled: true, cursor: 'not-allowed', title: NOT_CONFIGURED_TITLE } as const;
  }
  return { disabled: false, cursor: 'pointer', title: undefined } as const;
}

export function DraftCard({
  draft,
  configured,
  conversationId,
}: {
  draft: NeoChangeDraft;
  configured: boolean;
  conversationId: string | null;
}) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usedChangeId, setUsedChangeId] = useState<string | null>(draft.usedChangeId);
  const isNew = draft.kind === 'new';
  const withDraft = draft.id === null ? {} : { draftId: draft.id };
  const action = draftActionState(busy, configured);

  async function go() {
    setBusy(true);
    setError(null);
    try {
      if (isNew) {
        const res = await adminApi<{ change: NeoChange }>('/v1/admin/neo/changes', {
          method: 'POST',
          body: {
            title: draft.title,
            request: draft.request,
            ...(conversationId === null ? {} : { conversationId }),
            ...withDraft,
          },
        });
        navigate(`/admin/neo/changes/${res.change.id}`);
      } else {
        await adminApi(`/v1/admin/neo/changes/${draft.changeId}/revise`, {
          method: 'POST',
          body: { request: draft.request, ...withDraft },
        });
        navigate(`/admin/neo/changes/${draft.changeId}`);
      }
    } catch (err) {
      // Started already, from another tab or before a reload: show where it went.
      const used = usedChangeFromError(err);
      if (used !== null) {
        setUsedChangeId(used);
        setBusy(false);
        return;
      }
      setError(
        apiErrorMessage(
          err,
          isNew ? 'Could not start the change.' : 'Could not start the revision.'
        )
      );
      setBusy(false);
    }
  }

  return (
    <div className="neo-change-card">
      <div className="neo-change-card-head">
        <span className="admin-muted">{isNew ? 'New change' : 'Revision of'}</span>
        <strong>{draft.title}</strong>
      </div>
      <details>
        <summary>What the coding agent will be asked</summary>
        <NeoMarkdown content={draft.request} />
      </details>
      {usedChangeId !== null ? (
        <div className="neo-change-card-actions">
          <Link to={`/admin/neo/changes/${usedChangeId}`}>View change</Link>
          <span className="admin-muted">{isNew ? 'Started.' : 'Revision started.'}</span>
        </div>
      ) : (
        <div className="neo-change-card-actions">
          {/* A disabled button gets no hover events in some browsers, so the
              wrapper carries the tooltip and the button lets hover through. */}
          <span title={action.title} style={{ display: 'inline-flex', cursor: action.cursor }}>
            <button
              type="button"
              className="admin-button-affirm"
              disabled={action.disabled}
              style={{
                cursor: action.cursor,
                ...(action.cursor === 'not-allowed' ? { pointerEvents: 'none' } : {}),
              }}
              onClick={() => void go()}
            >
              {busy ? 'Starting…' : isNew ? 'Start' : 'Revise'}
            </button>
          </span>
          <span className="admin-muted">
            {configured
              ? 'Runs the coding agent on GitHub and opens a PR. Nothing merges without you.'
              : 'Neo changes are not configured on this server.'}
          </span>
        </div>
      )}
      {error !== null && <p className="admin-inline-error">{error}</p>}
    </div>
  );
}

/** One turn of the transcript, with the cards its reply drafted. */
export function AdminNeoTurn({
  message: m,
  sending,
  configured,
  conversationId,
}: {
  message: ChatMessage;
  sending: boolean;
  configured: boolean;
  conversationId: string | null;
}) {
  return (
    <div className={`neo-admin-turn${m.role === 'user' ? ' neo-admin-turn--user' : ''}`}>
      <div className="neo-admin-turn-head">
        <strong>{m.role === 'user' ? 'You' : 'Neo'}</strong>
      </div>
      {m.role === 'user' ? (
        <div className="neo-admin-turn-body">{m.content}</div>
      ) : m.content.length === 0 && sending ? (
        <div className="admin-muted">Neo is thinking…</div>
      ) : (
        <div className={m.error ? 'admin-inline-error' : undefined}>
          <NeoMarkdown content={m.content} />
        </div>
      )}
      {m.drafts.map((d, i) => (
        <DraftCard
          key={d.id ?? `${m.key}-${i}`}
          draft={d}
          configured={configured}
          conversationId={conversationId}
        />
      ))}
    </div>
  );
}

export function AdminNeoChat({ configured }: { configured: boolean }) {
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  // Set by Stop (TOM-671): the stream is closed, which stops the turn here.
  const stoppedRef = useRef(false);
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let ignore = false;
    adminApi<Conversation>('/v1/admin/neo/conversation')
      .then((res) => {
        if (ignore) return;
        setConversationId(res.conversationId);
        setMessages(parseStoredMessages(res.messages).map((m) => ({ ...m, key: nextKey() })));
      })
      .catch(() => undefined)
      .finally(() => {
        if (!ignore) setLoaded(true);
      });
    return () => {
      ignore = true;
      abortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages]);

  function patchLast(update: (m: ChatMessage) => ChatMessage) {
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last === undefined || last.role !== 'assistant') return prev;
      return [...prev.slice(0, -1), update(last)];
    });
  }

  async function send() {
    const content = input.trim();
    if (content.length === 0 || sending) return;
    stoppedRef.current = false;
    setInput('');
    setSending(true);
    setMessages((prev) => [
      ...prev,
      { key: nextKey(), role: 'user', content, drafts: [] },
      { key: nextKey(), role: 'assistant', content: '', drafts: [] },
    ]);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const res = await fetch('/v1/admin/neo/chat/stream', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          message: content,
          ...(conversationId === null ? {} : { conversationId }),
        }),
        credentials: 'same-origin',
        signal: controller.signal,
      });
      if (!res.ok || res.body === null) {
        const body = (await res.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? `Neo could not answer (${res.status}).`);
      }
      await readNeoStream(res.body, (event, data) => {
        if (controller.signal.aborted) return;
        switch (event) {
          case 'delta':
            patchLast((m) => ({ ...m, content: m.content + (data.text ?? '') }));
            break;
          case 'reset':
            patchLast((m) => ({ ...m, content: '' }));
            break;
          case 'replace':
            patchLast((m) => ({ ...m, content: data.content ?? '' }));
            break;
          case 'change_drafts': {
            const drafts = parseChangeDrafts(data.drafts);
            patchLast((m) => ({ ...m, drafts: [...m.drafts, ...drafts] }));
            break;
          }
          case 'done':
            if (typeof data.conversationId === 'string') setConversationId(data.conversationId);
            break;
          case 'error':
            patchLast((m) => ({
              ...m,
              content: data.message ?? 'Neo could not answer right now.',
              error: true,
            }));
            break;
        }
      });
    } catch (err) {
      if (stoppedRef.current) {
        patchLast((m) => ({
          ...m,
          content: m.content.length > 0 ? `${m.content}\n\n_Stopped._` : '_Stopped._',
        }));
      } else if (!controller.signal.aborted) {
        patchLast((m) => ({
          ...m,
          content: err instanceof Error ? err.message : 'Neo could not answer right now.',
          error: true,
        }));
      }
    } finally {
      setSending(false);
    }
  }

  function stop() {
    stoppedRef.current = true;
    abortRef.current?.abort();
  }

  function newChat() {
    abortRef.current?.abort();
    setConversationId(null);
    setMessages([]);
    setSending(false);
  }

  return (
    <div className="neo-change-chat">
      <div className="neo-change-chat-head">
        <h2>Ask Neo for a change</h2>
        <button
          type="button"
          className="admin-link-button"
          onClick={newChat}
          disabled={messages.length === 0}
        >
          New chat
        </button>
      </div>
      <div className="neo-admin-transcript neo-change-chat-log" aria-live="polite">
        {loaded && messages.length === 0 && (
          <p className="admin-hint">
            Describe what should change: a page, some wording, a behaviour. Neo writes it up as a
            card; Start runs a coding agent that opens a PR. You review it here and merge when CI is
            green.
          </p>
        )}
        {messages.map((m) => (
          <AdminNeoTurn
            key={m.key}
            message={m}
            sending={sending}
            configured={configured}
            conversationId={conversationId}
          />
        ))}
        <div ref={endRef} />
      </div>
      {/* The one chat box (TOM-671), with no + here: this chat takes no files. */}
      <ChatComposer
        className="neo-change-chat-form"
        value={input}
        onChange={setInput}
        onSubmit={() => void send()}
        placeholder='e.g. "On the home page, change the subtitle to …"'
        busy={sending}
        onStop={stop}
        // Up and Down bring back what the admin asked here (TOM-915).
        history={messages.filter((m) => m.role === 'user').map((m) => m.content)}
        testId="neo-change-chat-form"
      />
    </div>
  );
}
